import React, { useState, useEffect } from "react";
import { MdClose, MdCheck } from "react-icons/md";
import { CircularProgress } from "@mui/material";
import tw from "tailwind-styled-components";
import { Label, ModalHeader, ScrollableContent } from "../Styles/InputStyles";
import { GoSearch } from "react-icons/go";

export default function SinglePropSelectModal({
    isOpen,
    onClose,
    onSelect,
    options = [],
    selectedProperty = null,
    isLoading = false,
}) {
    const [tempSelectedProperty, setTempSelectedProperty] = useState(selectedProperty);
    const [searchText, setSearchText] = useState("");

    useEffect(() => {
        if (isOpen) {
            setTempSelectedProperty(selectedProperty);
            setSearchText("");
        }
    }, [isOpen, selectedProperty]);

    const handlePropertySelect = (property) => {
        setTempSelectedProperty(property);
        onSelect(property);
        onClose();
    };

    const filteredOptions = options
        .filter(property => property.id !== 0)
        .filter(property =>
            property.name?.toLowerCase().includes(searchText.toLowerCase()) ||
            property.gId?.toLowerCase().includes(searchText.toLowerCase())
        );

    const isSelected = (propertyId) => {
        return tempSelectedProperty?.id === propertyId;
    };

    const getPropertyType = (type) => {
        return type === 1 ? "PG" : "Flat";
    };

    const getBedInfo = (property) => {
        const totalBeds = property.bedCount || 0;
        const occupiedBeds = property.occupiedBedCount || 0;
        return `${occupiedBeds}/${totalBeds} beds`;
    };

    const getTenantInfo = (property) => {
        const tenantCount = property.tenantCount || 0;
        return `${tenantCount} tenant${tenantCount !== 1 ? 's' : ''}`;
    };

    if (!isOpen) return null;

    return (
        <Overlay>
            <ModalWrapper>
                <ModalHeader>
                    <h1 className="text-white text-lg font-medium">Select Property</h1>
                    <button onClick={onClose}>
                        <MdClose color="white" size={24} />
                    </button>
                </ModalHeader>

                <div className="p-4 relative">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search properties..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            className="w-full px-8 h-12 py-2 border border-gray-200 rounded-md focus:outline-none focus:border-primary-color text-gray-800 placeholder-gray-300"
                        />
                        <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                            <GoSearch className="w-4 h-4 text-gray-400" />
                        </div>
                    </div>
                </div>

                <ScrollableContent className="max-h-[calc(100dvh-140px)]">
                    {isLoading ? (
                        <div className="flex justify-center py-8">
                            <CircularProgress />
                        </div>
                    ) : filteredOptions.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            {options.filter(property => property.id !== 0).length === 0
                                ? "No properties available"
                                : "No properties found"
                            }
                        </div>
                    ) : (
                        <div className="px-4 space-y-2">
                            {filteredOptions.map((property) => (
                                <OptionRow
                                    key={property.id}
                                    onClick={() => handlePropertySelect(property)}
                                    selected={isSelected(property.id)}
                                >
                                    <div className="flex flex-col flex-1">
                                        <div className="flex items-center gap-2 mb-1 justify-between">
                                            <Label className="font-medium text-sm">
                                                {property.name}
                                            </Label>
                                            <span className={`text-xs px-2 py-1 rounded-full ${property.type === 1
                                                ? 'bg-blue-100 text-blue-700'
                                                : 'bg-purple-100 text-purple-700'
                                                }`}>
                                                {getPropertyType(property.type)}
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-2 justify-between">
                                            <div className="text-xs text-gray-500 mb-1">
                                                ID: {property.gId || property.id}
                                            </div>


                                            <div className="flex items-center gap-4 text-xs text-gray-600">
                                                <span className="bg-gray-100 px-2 py-1 rounded">
                                                    {getBedInfo(property)}
                                                </span>
                                                <span className="bg-gray-100 px-2 py-1 rounded">
                                                    {getTenantInfo(property)}
                                                </span>
                                            </div>
                                        </div>

                                    </div>

                                </OptionRow>
                            ))}
                        </div>
                    )}
                </ScrollableContent>
            </ModalWrapper>
        </Overlay>
    );
}

// Styled components
const Overlay = tw.div`fixed inset-0 z-50 flex items-center justify-end bg-black/50`;
const ModalWrapper = tw.div`bg-white w-11/12 h-full flex flex-col relative`;
const Header = tw.div`flex items-center justify-between bg-primary-color py-4 px-4`;
const OptionRow = tw.div`
    flex items-center justify-between p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50
    ${props => props.selected ? 'border-green-500 bg-green-50' : ''}
`;